import React, { memo } from 'react';
import ModuloCheques from './ModuloCheques';
import ModuloPlanPagos from './ModuloPlanPagos';
import { formatCurrency } from '../helpers';

const FORMAS_PAGO_DISPONIBLES = [
    'Transferencia Bancaria', 'Depósito', 'Cheque', 'Efectivo', 'Tarjeta de Crédito',
    'Débito Automático', 'Pagos mis cuentas', 'Mercado Pago', '12 cuotas - Tarjeta Supervielle',
    'Compensación de Deuda', 'Comisiones de Terceros'
];
const FORMAS_PAGO_IMPUESTOS = ['VEP', 'Transferencia Bancaria', 'Débito Automático'];
const ESTATUS_GASTO_OPCIONES = ['Pendiente de Pago', 'Parcialmente Pagado', 'Pagado'];

const SeccionTesoreria = ({ hookData }) => {
    const {
        formData, setFormData, calculos, esModoDirecto: esImpuesto, cuentas = [],
        pagosList = [], pagoInput, setPagoInput, chequesList = [], chequesBorrador = [],
        chequeInput, setChequeInput, cuotasBorrador = [], cuotaInput, setCuotaInput,
        handleCuentaOrigenChange, handleAddCuotaBorrador, handleRemoveCuotaBorrador,
        handleToggleCuotaPagada, handleRemoveCuota, handleAddPago, handleTogglePagoPagado,
        handleRemovePago, handleAddChequeBorrador, handleRemoveChequeBorrador,
        handleToggleChequePagados, handleRemoveChequeDefinitivo, handleRemoveTodosCheques,
        formatBlurInputAmount
    } = hookData;

    const monedaActual = formData.moneda || 'ARS';
    const simboloMoneda = monedaActual === 'USD' ? 'U$S' : '$';
    const monedaSimbolo = monedaActual === 'USD' ? 'USD' : 'ARS';

    const esFormaChequeSeleccionada = pagoInput.forma === 'Cheque';
    const esDebitoAutomaticoSeleccionado = (esImpuesto ? formData.formaPagoImpuesto : pagoInput.forma) === 'Débito Automático';

    const handleSelectFormaPago = (e) => {
        const forma = e.target.value;
        if (esImpuesto) {
            setFormData(prev => ({ 
                ...prev, 
                formaPagoImpuesto: forma, 
                esPlanPago: forma === 'Débito Automático' ? prev.esPlanPago : false 
            }));
        } else {
            setPagoInput(prev => ({ 
                ...prev, 
                forma,
                cuentaId: ['Transferencia Bancaria', 'Tarjeta de Crédito', 'Débito Automático', 'Pagos mis cuentas'].includes(forma) ? prev.cuentaId : ''
            }));
            if (forma !== 'Débito Automático') {
                setFormData(prev => ({ ...prev, esPlanPago: false }));
            }
        }
    };

    const estatusActual = formData.estatus || calculos.estatusCalculado;
    const chequesTodosPagados = chequesList.length > 0 && chequesList.every(c => c.pagada);
    const chequesAlgunoPagado = chequesList.some(c => c.pagada);

    const opcionesFormasPago = esImpuesto ? FORMAS_PAGO_IMPUESTOS : FORMAS_PAGO_DISPONIBLES;
    const valorFormaPago = esImpuesto ? formData.formaPagoImpuesto : pagoInput.forma;
    const requiereCuentaBancaria = ['Transferencia Bancaria', 'Tarjeta de Crédito', 'Débito Automático', 'Pagos mis cuentas', 'VEP'].includes(valorFormaPago);

    const SelectCuentaBancaria = () => (
        <div>
            <label className="block text-[14px] font-bold text-blue-900 mb-1.5 pl-1">Cuenta Bancaria Origen (Opcional)</label>
            <select 
                value={esImpuesto ? (formData.cuentaId || '') : pagoInput.cuentaId} 
                onChange={esImpuesto ? handleCuentaOrigenChange : (e => setPagoInput(prev => ({ ...prev, cuentaId: e.target.value })))} 
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold text-slate-800 bg-white h-[46px] outline-none cursor-pointer"
            >
                <option value="">Seleccionar Cuenta (Opcional)...</option>
                {cuentas.map(c => (
                    <option key={c.id} value={c.id}>
                        {c.nombre || `${c.banco} ${c.titular || ''} (${c.tipoCuenta || ''})`}
                    </option>
                ))}
            </select>
        </div>
    );

    return (
        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                Tesorería y Formas de Pago
            </legend>

            <div className="space-y-6 pt-2">
                <div className={`p-5 rounded-xl border space-y-4 ${esImpuesto ? 'bg-indigo-50/40 border-indigo-200' : 'bg-slate-50/60 border-slate-200'}`}>
                    <div className="flex justify-between items-center border-b border-slate-200/80 pb-2">
                        <span className="text-xs font-black text-slate-700 uppercase tracking-wider">
                            {esImpuesto ? 'Forma de Pago Impuesto / Préstamo' : 'Desglose de Formas de Pago'}
                        </span>
                        <span className="text-sm font-mono font-bold text-slate-800">
                            Asignado: <strong className="font-mono text-base font-black text-indigo-900">{formatCurrency(calculos.totalPagadoAsignado, monedaSimbolo)}</strong> / {formatCurrency(calculos.montoTotal, monedaSimbolo)}
                        </span>
                    </div>

                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                            <div>
                                <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Forma de Pago *</label>
                                <select
                                    value={valorFormaPago || ''}
                                    onChange={handleSelectFormaPago}
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 cursor-pointer"
                                >
                                    <option value="">Seleccionar...</option>
                                    {opcionesFormasPago.map(f => <option key={f} value={f}>{f}</option>)}
                                </select>
                            </div>

                            {/* BOTÓN CONMUTADOR DE PLAN DE CUOTAS (Visible si es Débito Automático) */}
                            {esDebitoAutomaticoSeleccionado && (
                                <div 
                                    onClick={() => setFormData(prev => ({ ...prev, esPlanPago: !prev.esPlanPago }))}
                                    className={`h-[46px] px-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between select-none ${
                                        formData.esPlanPago
                                            ? 'bg-indigo-100/80 border-indigo-300 ring-2 ring-indigo-500/20 shadow-xs'
                                            : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`}
                                >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <div className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 transition-all ${
                                            formData.esPlanPago ? 'bg-indigo-600 border-indigo-600 text-white shadow-xs' : 'bg-white border-slate-300'
                                        }`}>
                                            {formData.esPlanPago && (
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                        <div className="flex flex-col truncate">
                                            <span className="text-xs font-bold text-indigo-950 truncate leading-tight uppercase">🏛️ Generar Plan de Cuotas</span>
                                            <span className="text-[9px] font-semibold text-slate-400 truncate">Desglosa en múltiples vencimientos</span>
                                        </div>
                                    </div>
                                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border shrink-0 transition-all ${
                                        formData.esPlanPago ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-100 text-slate-400 border-slate-200'
                                    }`}>
                                        {formData.esPlanPago ? 'Habilitado' : 'No'}
                                    </span>
                                </div>
                            )}

                            {/* MONTO ASIGNADO: Muestra si NO es cheque Y (NO es Plan de Pago en Cuotas) */}
                            {!esFormaChequeSeleccionada && !formData.esPlanPago && (
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Monto Asignado ({simboloMoneda})</label>
                                    <input
                                        type="text"
                                        value={pagoInput.monto}
                                        onChange={e => setPagoInput(prev => ({ ...prev, monto: e.target.value }))}
                                        onBlur={() => formatBlurInputAmount(pagoInput.monto, (val) => setPagoInput(prev => ({ ...prev, monto: val })))}
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-base font-mono font-bold text-slate-900 outline-none bg-white h-[46px] focus:border-blue-400"
                                        placeholder="0,00"
                                    />
                                </div>
                            )}
                        </div>

                        {/* CUENTA BANCARIA CONDICIONAL */}
                        {((!esDebitoAutomaticoSeleccionado && requiereCuentaBancaria) || (esDebitoAutomaticoSeleccionado)) && (
                            <div className="animate-fade-in">
                                <SelectCuentaBancaria />
                            </div>
                        )}

                        {/* MÓDULOS DE CHEQUES Y PLAN DE PAGOS */}
                        {!esImpuesto && esFormaChequeSeleccionada && (
                            <ModuloCheques
                                chequesBorrador={chequesBorrador}
                                chequeInput={chequeInput}
                                setChequeInput={setChequeInput}
                                cuentas={cuentas}
                                moneda={monedaActual}
                                handleAddChequeBorrador={handleAddChequeBorrador}
                                handleRemoveChequeBorrador={handleRemoveChequeBorrador}
                                formatBlurInputAmount={formatBlurInputAmount}
                            />
                        )}

                        {esDebitoAutomaticoSeleccionado && formData.esPlanPago && (
                            <ModuloPlanPagos
                                cuotasBorrador={cuotasBorrador}
                                cuotaInput={cuotaInput}
                                setCuotaInput={setCuotaInput}
                                moneda={monedaActual}
                                handleAddCuotaBorrador={handleAddCuotaBorrador}
                                handleRemoveCuotaBorrador={handleRemoveCuotaBorrador}
                                formatBlurInputAmount={formatBlurInputAmount}
                            />
                        )}

                        {/* BOTÓN CONFIRMAR */}
                        {valorFormaPago && (
                            <button
                                type="button"
                                onClick={handleAddPago}
                                className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold uppercase text-xs transition-all shadow-md active:scale-98 cursor-pointer flex items-center justify-center gap-1.5"
                            >
                                {esFormaChequeSeleccionada 
                                    ? `+ Confirmar y Agregar Pago con Cheque(s) (${chequesBorrador.length})` 
                                    : formData.esPlanPago
                                        ? `+ Confirmar y Agregar Plan de Pagos en Cuotas (${cuotasBorrador.length})`
                                        : '+ Agregar Pago'
                                }
                            </button>
                        )}
                    </div>

                    {/* LISTADOS ASIGNADOS */}
                    {(pagosList.length > 0 || chequesList.length > 0 || (formData.cuotasPlan && formData.cuotasPlan.length > 0)) && (
                        <div className="space-y-3 pt-3 border-t border-slate-200">
                            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-1">Pagos Asignados</span>
                            
                            {pagosList.map((p, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-200 text-xs font-semibold shadow-xs">
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <span className="font-bold text-blue-900 uppercase shrink-0">{p.formaPago}</span>
                                        {p.cuentaNombre && <span className="text-slate-500 font-normal uppercase truncate">({p.cuentaNombre})</span>}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className="font-mono text-base font-black text-slate-950">{formatCurrency(p.monto, monedaSimbolo)}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleTogglePagoPagado(idx)}
                                            className={`px-3 py-1 rounded-full text-[10px] font-black uppercase transition-all cursor-pointer ${
                                                p.pagado ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                            }`}
                                        >
                                            {p.pagado ? '✓ Pagado' : '⏳ Pendiente'}
                                        </button>
                                        <button type="button" onClick={() => handleRemovePago(idx)} className="text-rose-500 hover:text-rose-800 font-bold cursor-pointer p-1">✕</button>
                                    </div>
                                </div>
                            ))}

                            {/* CUOTAS RENDERIZADAS */}
                            {formData.cuotasPlan && formData.cuotasPlan.length > 0 && (
                                <div className="bg-indigo-50/30 border border-indigo-200 rounded-xl overflow-hidden shadow-xs">
                                    <div className="bg-indigo-100/70 p-3 flex items-center justify-between border-b border-indigo-200">
                                        <span className="font-black text-indigo-950 uppercase text-xs">🏛️ PLAN DE PAGOS EN CUOTAS ({formData.cuotasPlan.length})</span>
                                    </div>
                                    <div className="p-3 space-y-2 bg-white/60">
                                        {formData.cuotasPlan.map((c, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-indigo-100 text-xs shadow-2xs pl-4 border-l-4 border-l-indigo-600">
                                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                    <span className="font-bold text-indigo-950 uppercase shrink-0">Cuota N° {c.numero}</span>
                                                    {c.cuentaNombre && <span className="text-slate-500 font-normal uppercase truncate">({c.cuentaNombre})</span>}
                                                    <span className="text-slate-500 shrink-0">Vence: <strong className="text-slate-800 font-mono">{c.fechaVencimiento}</strong></span>
                                                </div>
                                                <div className="flex items-center gap-3 shrink-0">
                                                    <span className="font-mono font-bold text-indigo-950">{formatCurrency(c.monto, monedaSimbolo)}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleCuotaPagada(idx)}
                                                        className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase transition-all cursor-pointer ${
                                                            c.pagada ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                                        }`}
                                                    >
                                                        {c.pagada ? '✓ Pagado' : '⏳ Pendiente'}
                                                    </button>
                                                    <button type="button" onClick={() => handleRemoveCuota(idx)} className="text-rose-400 hover:text-rose-700 font-bold p-0.5">✕</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* CHEQUES RENDERIZADOS */}
                            {!esImpuesto && chequesList.length > 0 && (
                                <div className="bg-amber-50/20 border border-amber-300/80 rounded-xl overflow-hidden shadow-xs">
                                    <div className="bg-amber-100/60 p-3 flex items-center justify-between border-b border-amber-200/80">
                                        <div className="flex items-center gap-2">
                                            <span className="font-black text-amber-950 uppercase text-xs">📜 CHEQUES ({chequesList.length})</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-base font-black text-amber-950">{formatCurrency(calculos.totalChequesAsignados, monedaSimbolo)}</span>
                                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${
                                                chequesTodosPagados ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : chequesAlgunoPagado ? 'bg-blue-100 text-blue-800 border border-blue-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                            }`}>
                                                {chequesTodosPagados ? '✓ Todos Pagados' : chequesAlgunoPagado ? '⏳ Parcial Pagado' : '⏳ Pendiente'}
                                            </span>
                                            <button type="button" onClick={handleRemoveTodosCheques} className="text-rose-500 hover:text-rose-800 font-bold p-1">✕</button>
                                        </div>
                                    </div>
                                    <div className="p-3 space-y-2 bg-white/60">
                                        {chequesList.map((chk, idx) => {
                                            const estaPagado = !!chk.pagada;
                                            return (
                                                <div key={idx} className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-amber-200/60 text-xs shadow-2xs pl-4 border-l-4 border-l-amber-500">
                                                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                        <span className="text-amber-400 font-black">└─</span>
                                                        <span className="font-mono font-bold text-slate-900 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-[11px]">{chk.numero}</span>
                                                        <span className="text-slate-700 font-semibold uppercase truncate">{chk.cuentaNombre || chk.banco || 'S/D'}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3 shrink-0">
                                                        <span className="font-mono font-bold text-amber-900">{formatCurrency(chk.monto, monedaSimbolo)}</span>
                                                        <span className="text-[10px] font-medium text-slate-400 uppercase hidden sm:inline">Vence: <strong className="text-slate-700 font-mono">{chk.fechaVencimiento}</strong></span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleChequePagados(idx)}
                                                            className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase transition-all cursor-pointer ${
                                                                estaPagado ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                                            }`}
                                                        >
                                                            {estaPagado ? '✓ Pagado' : '⏳ Pendiente'}
                                                        </button>
                                                        <button type="button" onClick={() => handleRemoveChequeDefinitivo(idx)} className="text-rose-400 hover:text-rose-700 font-bold p-0.5">✕</button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* EGRESO DEBITADO Y AUDITORÍA */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="md:col-span-2">
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Egreso Real Debitado Banco ({simboloMoneda}) [Calculado]</label>
                        <input
                            type="text"
                            disabled
                            value={formatCurrency(calculos.egresoRealBanco, monedaSimbolo)}
                            className="w-full border rounded-xl px-4 py-2.5 text-base font-mono font-bold outline-none h-[46px] bg-slate-100/80 text-slate-800 border-slate-200 cursor-not-allowed shadow-2xs"
                        />
                    </div>

                    {!esImpuesto && (
                        <div 
                            onClick={() => setFormData(prev => ({ ...prev, forzarConciliacion: !prev.forzarConciliacion }))}
                            className={`h-[46px] px-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between select-none ${
                                formData.forzarConciliacion ? 'bg-blue-50/90 border-blue-300 ring-2 ring-blue-500/20 shadow-xs' : 'bg-white border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <div className="flex items-center gap-2.5 min-w-0">
                                <div className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 transition-all ${
                                    formData.forzarConciliacion ? 'bg-blue-600 border-blue-600 text-white shadow-xs' : 'bg-white border-slate-300'
                                }`}>
                                    {formData.forzarConciliacion && (
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                                <div className="flex flex-col truncate">
                                    <span className="text-xs font-bold text-slate-800 truncate leading-tight">Forzar Conciliación 100%</span>
                                    <span className="text-[9px] font-semibold text-slate-400 truncate">Excluye Desfase</span>
                                </div>
                            </div>
                            <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border shrink-0 transition-all ${
                                formData.forzarConciliacion ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-100 text-slate-400 border-slate-200'
                            }`}>
                                {formData.forzarConciliacion ? 'Si' : 'No'}
                            </span>
                        </div>
                    )}
                </div>

                <div className="p-5 rounded-2xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50/50 via-white to-slate-50 shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b border-indigo-100 pb-2.5">
                        <span className="text-xs font-black text-slate-800 uppercase tracking-wider">Auditoría de Tesorería ({monedaActual})</span>
                        <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full uppercase tracking-widest">Estado Financiero</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
                        <div className="bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-between shadow-xs">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Devengado</span>
                            <span className="font-mono text-lg font-black text-indigo-950 mt-1">{formatCurrency(calculos.montoTotal, monedaSimbolo)}</span>
                            <span className="text-[10px] font-semibold text-slate-400 mt-1">Importe total a liquidar</span>
                        </div>

                        <div className="bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-between shadow-xs">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Debitado Real</span>
                            <span className="font-mono text-lg font-black text-slate-900 mt-1">{calculos.egresoRealBanco > 0 ? formatCurrency(calculos.egresoRealBanco, monedaSimbolo) : '---'}</span>
                            <span className="text-[10px] font-semibold text-slate-400 mt-1">Pagos + Cheques + Cuotas</span>
                        </div>

                        <div className="flex flex-col justify-center">
                            <div className={`h-full px-4 py-3 rounded-xl border text-center font-mono font-black text-xs shadow-xs flex flex-col items-center justify-center ${
                                calculos.diferenciaEgreso === 0 ? 'bg-emerald-50/70 text-emerald-800 border-emerald-500' : calculos.diferenciaEgreso < 0 ? 'bg-amber-500 text-white border-amber-600' : 'bg-rose-500 text-white border-rose-600'
                            }`}>
                                {calculos.diferenciaEgreso === 0 ? (
                                    <span className="text-sm uppercase tracking-wider flex items-center gap-1">✓ Conciliado 100%</span>
                                ) : (
                                    <>
                                        <span className="text-[10px] uppercase font-extrabold opacity-90 tracking-wider">
                                            {calculos.diferenciaEgreso < 0 ? 'Excedente en Débito' : 'Pendiente de Conciliar'}
                                        </span>
                                        <span className="text-base font-black mt-0.5">{formatCurrency(Math.abs(calculos.diferenciaEgreso), monedaSimbolo)}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Estatus de Pago *</label>
                    <select
                        value={estatusActual}
                        onChange={e => setFormData(prev => ({ ...prev, estatus: e.target.value }))}
                        className={`w-full border rounded-xl px-4 py-2.5 text-sm font-black outline-none h-[46px] transition-all uppercase cursor-pointer ${
                            estatusActual === 'Pagado' ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : estatusActual === 'Parcialmente Pagado' ? 'bg-blue-50 text-blue-800 border-blue-300' : 'bg-amber-50 text-amber-800 border-amber-300'
                        }`}
                    >
                        {ESTATUS_GASTO_OPCIONES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            </div>
        </fieldset>
    );
};

export default memo(SeccionTesoreria);